import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Shield, Lock, Eye, Users } from 'lucide-react';

const PrivacyPolicy = () => {
  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <div className="container mx-auto px-4">
        <div className="max-w-4xl mx-auto bg-white rounded-2xl shadow-lg p-8 md:p-12">
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-[#2D9A68] hover:text-[#237852] mb-6 transition-colors"
          >
            <ArrowLeft size={20} />
            Retour à l'accueil
          </Link>

          <div className="text-center mb-8">
            <Shield className="inline-block text-[#2D9A68] mb-4" size={48} />
            <h1 className="text-4xl font-bold text-gray-900 mb-4">Politique de Confidentialité</h1>
            <p className="text-gray-600">
              Chez Climatpac 81, nous respectons votre vie privée et protégeons vos données personnelles
            </p>
          </div>

          <div className="space-y-8 text-gray-700">
            {/* Introduction */}
            <section className="bg-[#2D9A68]/5 rounded-xl p-6">
              <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                <Lock className="text-[#2D9A68]" size={24} />
                Introduction
              </h2>
              <p className="mb-4">
                La présente politique de confidentialité a pour but de vous informer sur la manière dont Climatpac 81
                collecte, utilise et protège vos données personnelles conformément au Règlement Général sur la Protection
                des Données (RGPD) et à la loi « Informatique et Libertés ».
              </p>
              <p>
                <strong>Responsable du traitement :</strong> Climatpac 81<br />
                <strong>Contact :</strong> <a href="mailto:contact@climatpac81.fr" className="text-[#2D9A68] hover:underline">contact@climatpac81.fr</a><br />
                <strong>Téléphone :</strong> <a href="tel:0188617508" className="text-[#2D9A68] hover:underline">01 88 61 75 08</a>
              </p>
            </section>

            {/* Données collectées */}
            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                <Eye className="text-[#2F5BA0]" size={24} />
                1. Données collectées
              </h2>
              <p className="mb-4">Nous collectons les données personnelles suivantes :</p>

              <div className="space-y-4">
                <div className="bg-gray-50 rounded-lg p-4">
                  <h3 className="font-bold text-gray-900 mb-2">Via le test d'éligibilité aux aides :</h3>
                  <ul className="list-disc list-inside space-y-1 ml-4">
                    <li>Nom et prénom</li>
                    <li>Adresse email</li>
                    <li>Numéro de téléphone (optionnel)</li>
                    <li>Code postal</li>
                    <li>Type de logement et surface</li>
                    <li>Année de construction</li>
                    <li>Type de chauffage actuel</li>
                    <li>Nombre d'occupants</li>
                    <li>Revenu fiscal de référence</li>
                    <li>Types de travaux souhaités</li>
                  </ul>
                </div>

                <div className="bg-gray-50 rounded-lg p-4">
                  <h3 className="font-bold text-gray-900 mb-2">Via le formulaire de contact :</h3>
                  <ul className="list-disc list-inside space-y-1 ml-4">
                    <li>Nom et prénom</li>
                    <li>Adresse email</li>
                    <li>Numéro de téléphone (optionnel)</li>
                    <li>Sujet et message</li>
                  </ul>
                </div>

                <div className="bg-gray-50 rounded-lg p-4">
                  <h3 className="font-bold text-gray-900 mb-2">Données de navigation :</h3>
                  <ul className="list-disc list-inside space-y-1 ml-4">
                    <li>Adresse IP</li>
                    <li>Type de navigateur</li>
                    <li>Pages visitées</li>
                    <li>Date et heure de connexion</li>
                  </ul>
                </div>
              </div>
            </section>

            {/* Finalités */}
            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">2. Finalités du traitement</h2>
              <p className="mb-4">Vos données personnelles sont collectées pour les finalités suivantes :</p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li><strong>Test d'éligibilité :</strong> Calculer le montant des aides auxquelles vous êtes éligible (MaPrimeRénov', CEE)</li>
                <li><strong>Communication :</strong> Vous envoyer les résultats du test d'éligibilité par email</li>
                <li><strong>Suivi commercial :</strong> Vous recontacter pour établir un devis personnalisé</li>
                <li><strong>Service client :</strong> Répondre à vos demandes de renseignements</li>
                <li><strong>Amélioration du service :</strong> Analyser l'utilisation du site pour l'améliorer</li>
                <li><strong>Obligations légales :</strong> Respecter nos obligations légales et réglementaires</li>
              </ul>
            </section>

            {/* Base légale */}
            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">3. Base légale du traitement</h2>
              <p className="mb-4">Le traitement de vos données repose sur :</p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li><strong>Votre consentement :</strong> En remplissant nos formulaires, vous consentez au traitement de vos données</li>
                <li><strong>L'exécution d'un contrat :</strong> Pour répondre à votre demande de devis ou d'informations</li>
                <li><strong>Intérêt légitime :</strong> Pour améliorer nos services et assurer le suivi commercial</li>
              </ul>
            </section>

            {/* Destinataires */}
            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                <Users className="text-[#E6A835]" size={24} />
                4. Destinataires des données
              </h2>
              <p className="mb-4">Vos données personnelles sont destinées à :</p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li>Les collaborateurs habilités de Climatpac 81</li>
                <li>Nos sous-traitants techniques (hébergement, maintenance du site)</li>
                <li>Les organismes financeurs des aides (si vous réalisez des travaux avec nous)</li>
              </ul>
              <p className="mt-4">
                <strong>Nous ne vendons ni ne louons vos données personnelles à des tiers.</strong>
              </p>
            </section>

            {/* Durée de conservation */}
            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">5. Durée de conservation</h2>
              <p className="mb-4">Vos données sont conservées pendant :</p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li><strong>Tests d'éligibilité et demandes de contact :</strong> 3 ans à compter de votre dernier contact</li>
                <li><strong>Clients ayant réalisé des travaux :</strong> 10 ans (garantie décennale)</li>
                <li><strong>Données de navigation :</strong> 13 mois maximum</li>
              </ul>
            </section>

            {/* Droits */}
            <section className="bg-[#2D9A68]/5 rounded-xl p-6">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">6. Vos droits</h2>
              <p className="mb-4">Conformément au RGPD, vous disposez des droits suivants :</p>
              <ul className="space-y-3">
                <li className="flex items-start gap-2">
                  <span className="font-bold text-[#2D9A68] mt-1">✓</span>
                  <div>
                    <strong>Droit d'accès :</strong> Obtenir la confirmation que vos données sont traitées et accéder à vos données
                  </div>
                </li>
                <li className="flex items-start gap-2">
                  <span className="font-bold text-[#2D9A68] mt-1">✓</span>
                  <div>
                    <strong>Droit de rectification :</strong> Corriger des données inexactes ou incomplètes
                  </div>
                </li>
                <li className="flex items-start gap-2">
                  <span className="font-bold text-[#2D9A68] mt-1">✓</span>
                  <div>
                    <strong>Droit à l'effacement :</strong> Supprimer vos données dans certaines conditions
                  </div>
                </li>
                <li className="flex items-start gap-2">
                  <span className="font-bold text-[#2D9A68] mt-1">✓</span>
                  <div>
                    <strong>Droit d'opposition :</strong> Vous opposer au traitement de vos données pour des raisons tenant à votre situation
                  </div>
                </li>
                <li className="flex items-start gap-2">
                  <span className="font-bold text-[#2D9A68] mt-1">✓</span>
                  <div>
                    <strong>Droit à la limitation :</strong> Limiter le traitement de vos données dans certains cas
                  </div>
                </li>
                <li className="flex items-start gap-2">
                  <span className="font-bold text-[#2D9A68] mt-1">✓</span>
                  <div>
                    <strong>Droit à la portabilité :</strong> Recevoir vos données dans un format structuré
                  </div>
                </li>
              </ul>

              <div className="mt-6 p-4 bg-white rounded-lg">
                <p className="font-bold mb-2">Pour exercer ces droits :</p>
                <ul className="space-y-1">
                  <li>📧 Email : <a href="mailto:contact@climatpac81.fr" className="text-[#2D9A68] hover:underline">contact@climatpac81.fr</a></li>
                  <li>📞 Téléphone : <a href="tel:0188617508" className="text-[#2D9A68] hover:underline">01 88 61 75 08</a></li>
                </ul>
                <p className="mt-4 text-sm text-gray-600">
                  Vous pouvez également introduire une réclamation auprès de la CNIL : 
                  <a href="https://www.cnil.fr" target="_blank" rel="noopener noreferrer" className="text-[#2D9A68] hover:underline ml-1">
                    www.cnil.fr
                  </a>
                </p>
              </div>
            </section>

            {/* Sécurité */}
            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">7. Sécurité des données</h2>
              <p className="mb-4">
                Nous mettons en œuvre des mesures techniques et organisationnelles appropriées pour protéger vos données contre :
              </p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li>La destruction accidentelle ou illicite</li>
                <li>La perte accidentelle</li>
                <li>L'altération, la diffusion ou l'accès non autorisés</li>
              </ul>
              <p className="mt-4">
                Nos mesures de sécurité incluent : chiffrement SSL, sauvegardes régulières, accès restreints aux données.
              </p>
            </section>

            {/* Cookies */}
            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">8. Cookies</h2>
              <p className="mb-4">
                Notre site utilise des cookies pour améliorer votre expérience de navigation et réaliser des statistiques de visites.
              </p>
              <p className="mb-4">
                <strong>Types de cookies utilisés :</strong>
              </p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li><strong>Cookies techniques :</strong> Nécessaires au fonctionnement du site</li>
                <li><strong>Cookies analytiques :</strong> Pour comprendre comment vous utilisez le site</li>
              </ul>
              <p className="mt-4">
                Vous pouvez refuser les cookies en configurant votre navigateur. Cela peut limiter certaines fonctionnalités du site.
              </p>
            </section>

            {/* Modifications */}
            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">9. Modifications de la politique</h2>
              <p>
                Nous nous réservons le droit de modifier cette politique de confidentialité à tout moment.
                Toute modification sera publiée sur cette page avec la date de mise à jour.
              </p>
            </section>

            {/* Contact */}
            <section className="bg-[#2F5BA0]/5 rounded-xl p-6">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">10. Nous contacter</h2>
              <p className="mb-4">
                Pour toute question concernant cette politique de confidentialité ou le traitement de vos données personnelles :
              </p>
              <div className="space-y-2">
                <p>📧 <strong>Email :</strong> <a href="mailto:contact@climatpac81.fr" className="text-[#2D9A68] hover:underline">contact@climatpac81.fr</a></p>
                <p>📞 <strong>Téléphone :</strong> <a href="tel:0188617508" className="text-[#2D9A68] hover:underline">01 88 61 75 08</a></p>
                <p>📍 <strong>Zone d'intervention :</strong> Paris + 250 km</p>
              </div>
            </section>
          </div>

          <div className="mt-12 pt-8 border-t text-center text-gray-500 text-sm">
            <p>Dernière mise à jour : {new Date().toLocaleDateString('fr-FR')}</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;
